import holviSrc from '@assets/Holvi_Wordmark.png'
import {
  AccountSelector,
  Alert,
  AnimatedContentLoader,
  Badge,
  Button,
  Input,
  Switch,
  TextTooltip
} from '@components'
import { FeatureWrapper, useBusinessContext } from '@context'
import {
  SlideContent,
  SlideFooter,
  SlideText,
  SlideTitle,
  Spacer
} from '@pages/BankIntegrationOnboardingPage/slides/slide.styled'

import { tryFormatIBAN } from '@utils/iban'
import React, { useMemo, useState } from 'react'
import { Controller, get, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaArrowsAltH, FaCheck, FaShieldAlt } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled, { useTheme } from 'styled-components'
import {
  fetchBankIntegrations,
  invalidateFirstTransactionFetchNotice,
  useCurrentUser
} from '@query'
import {
  deleteHolviIntegration,
  fetchHolviAccounts,
  IHolviAccount,
  updateHolviAccount
} from '@query/holvi.ts'
import { isHttpError } from '@query/root'
import { AccountType } from '@constants'
import {
  extractGlobalApiErrors,
  firstApiErrorMessage,
  formatDate,
  getApiFieldErrors,
  setAPIErrors
} from '@utils'
import dayjs from 'dayjs'
import { UserPicker } from '@containers/UserPicker'
import { FaUserCheck, FaUserPlus } from 'react-icons/fa6'
import { getHolviAccountName } from '@root/utils/holvi'

interface Props {
  isEdit: boolean
  goBack: () => void
  onDone: () => void
}

const FINALIZE_FORM_ID = 'finalize-form'

const toAccountId = (value: unknown): number | null => {
  const id = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(id) || id <= 0) return null
  return id
}

const uniqueMessages = (messages: string[]): string[] => {
  const seen = new Set<string>()
  return messages.filter(message => {
    if (!message || seen.has(message)) return false
    seen.add(message)
    return true
  })
}

const holviErrorMessages = (
  data: unknown,
  fieldErrors: ReturnType<typeof getApiFieldErrors>
): string[] => {
  if (typeof data === 'string' && data.trim()) return [data]

  const fieldMessages = Object.values(fieldErrors ?? {}).flatMap(value => {
    const message = firstApiErrorMessage(value)
    return message ? [message] : []
  })

  return [...extractGlobalApiErrors(data), ...fieldMessages]
}

export const HolviEditSlide: React.FC<Props> = ({ isEdit, onDone, goBack }) => {
  const { t } = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUser()
  const theme = useTheme()

  const [submitMessages, setSubmitMessages] = useState<string[] | null>(null)
  const { isLoading: isAccountsLoading, refetch } = useQuery({
    queryKey: [slug, 'holvi', 'accounts'],
    queryFn: () => fetchHolviAccounts({ slug }),
    ...{
      enabled: business?.holvi_bookkeeping_api_enabled
    }
  })
  const { data: bankIntegrations } = useQuery({
    queryKey: [slug, 'bank_integrations'],
    queryFn: () => fetchBankIntegrations({ slug })
  })

  const {
    handleSubmit,
    register,
    control,
    setError,
    clearErrors,
    getValues,
    formState: { errors, isSubmitting, isLoading: isFormLoading }
  } = useForm<{ accounts: IHolviAccount[] }>({
    defaultValues: async () => {
      const { data } = await refetch()
      return { accounts: data?.results }
    }
  })
  const { fields: accountFields } = useFieldArray({
    control,
    name: 'accounts',
    keyName: '__id'
  })

  const onSubmit = handleSubmit(
    async data => {
      clearErrors()
      setSubmitMessages(null)

      const results = await Promise.allSettled(
        data.accounts.map(async (account, idx) => {
          try {
            await updateHolviAccount({ slug, accountId: account.id }, account)
          } catch (err) {
            throw { err, idx }
          }
        })
      )

      const messages: string[] = []
      let failed = false
      for (const result of results) {
        if (result.status === 'fulfilled') continue
        failed = true
        const { err, idx } = result.reason as { err: unknown; idx: number }
        if (isHttpError(err) && err.status === 400) {
          const fieldErrors = getApiFieldErrors(err.data)
          if (fieldErrors) {
            setAPIErrors(fieldErrors, setError, `accounts.${idx}`)
          }
          const fieldMessages = holviErrorMessages(err.data, fieldErrors)
          messages.push(
            ...(fieldMessages.length > 0 ? fieldMessages : [t('general.formValidationError')])
          )
        } else {
          messages.push(t('general.error'))
        }
      }

      if (failed) {
        setSubmitMessages(uniqueMessages(messages))
        return
      }

      await invalidateFirstTransactionFetchNotice(queryClient, slug)
      await onDone()
    },
    () => {
      setSubmitMessages([t('general.formValidationError')])
    }
  )

  const { mutateAsync: deleteIntegration, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteHolviIntegration({ slug }),
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'holvi'] })
        ])
      }
    }
  })

  const handleGoBack = async () => {
    if (!isEdit) {
      await deleteIntegration()
    }
    goBack()
  }

  const getAccountFilters = (account: IHolviAccount): Record<string, string> => {
    switch (account.type) {
      case 'psd':
        return { type: AccountType.ASS_PAY }
      case 'credit':
        return { type: AccountType.LIA_DUE }
      default:
        return {}
    }
  }

  const watchedAccounts = useWatch({
    control,
    name: 'accounts'
  })
  const today = dayjs()
  const todayISO = today.format('YYYY-MM-DD')
  const todayLabel = formatDate(today)
  const linkedBankAccountIds = useMemo(() => {
    const ids = new Set<number>()
    for (const integration of bankIntegrations?.results ?? []) {
      if (!integration.enabled) continue
      for (const account of integration.accounts ?? []) {
        if (!account.enabled) continue
        const id = toAccountId(account.account_id)
        if (id) ids.add(id)
      }
    }
    return ids
  }, [bankIntegrations])

  const ledgerAccountConflict = (
    accounts: IHolviAccount[] | undefined,
    index: number,
    accountId: unknown
  ) => {
    const id = toAccountId(accountId)
    if (!id) return true
    if (linkedBankAccountIds.has(id)) {
      return t('bankOnboarding.finalize.accountAlreadyLinked')
    }
    const usedOnAnotherRow = (accounts ?? []).some(
      (other, otherIndex) =>
        otherIndex !== index && Boolean(other?.enabled) && toAccountId(other?.account) === id
    )
    if (usedOnAnotherRow) {
      return t('bankOnboarding.finalize.accountUsedOnAnotherRow')
    }
    return true
  }

  return (
    <>
      <SlideContent>
        <StyledForm id={FINALIZE_FORM_ID} onSubmit={onSubmit}>
          <AnimatedContentLoader isLoading={isAccountsLoading || isFormLoading}>
            {isEdit ? (
              <SlideTitle>
                {t('bankOnboarding.finalize.editTitle', { bankName: 'Holvi' })}
              </SlideTitle>
            ) : (
              <SlideTitle>
                {t('bankOnboarding.finalize.createTitle', { bankName: 'Holvi' })}
              </SlideTitle>
            )}
            <SlideText>{t('bankOnboarding.finalize.description')}</SlideText>
            <LogoWrapper>
              <LogoImg $src={holviSrc} className="left" />
              <FaArrowsAltH />
              <LogoImg $src={theme.assets.logo} className="right" />
            </LogoWrapper>

            <Spacer />

            {submitMessages && (
              <Alert
                type="error"
                marginBottom="1rem"
                description={
                  submitMessages.length === 1 ? (
                    submitMessages[0]
                  ) : (
                    <SubmitErrorList>
                      {submitMessages.map(message => (
                        <li key={message}>{message}</li>
                      ))}
                    </SubmitErrorList>
                  )
                }
              />
            )}

            {accountFields.map((account, index) => {
              return (
                <StyledAccountWrapper key={`account-${account.id}`}>
                  <StyledAccountHeaderWrapper>
                    <label htmlFor={`accounts-${index}-enabled-switch`}>
                      <StyledAccountTitle>
                        {getHolviAccountName(account.name, account.type, t)}
                      </StyledAccountTitle>
                      <StyledAccountIdentifier>
                        {tryFormatIBAN(account.iban)}
                      </StyledAccountIdentifier>
                    </label>

                    <Controller
                      control={control}
                      name={`accounts.${index}.enabled`}
                      render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                        <Switch
                          label=""
                          height={24}
                          id={`accounts-${index}-enabled-switch`}
                          ref={ref}
                          checked={value}
                          onChange={onChange}
                          error={error}
                        />
                      )}
                    />
                  </StyledAccountHeaderWrapper>

                  {watchedAccounts?.[index]?.enabled && (
                    <>
                      <Controller
                        control={control}
                        name={`accounts.${index}.account`}
                        rules={{
                          required: { value: true, message: t('validation.required') },
                          validate: value =>
                            ledgerAccountConflict(getValues('accounts'), index, value)
                        }}
                        render={({ field: { ref, value, onChange }, fieldState: { error } }) => {
                          const clientConflict = ledgerAccountConflict(
                            watchedAccounts,
                            index,
                            value
                          )
                          const accountError = error?.message
                            ? error
                            : clientConflict === true
                            ? undefined
                            : { type: 'validate', message: clientConflict }

                          return (
                            <AccountSelector
                              id={`accounts-${index}-account-select`}
                              label={t('bankOnboarding.finalize.accountLabel')}
                              ref={ref}
                              error={error}
                              value={value}
                              onChange={nextValue => {
                                onChange(nextValue)
                                clearErrors(`accounts.${index}.account`)
                                setSubmitMessages(null)
                              }}
                              required={true}
                              errors={accountError}
                              accountFilter={getAccountFilters(account)}
                              allowCreate={true}
                            />
                          )
                        }}
                      />

                      <Input
                        type="date"
                        label={t('settings.integrations.bank.minSyncDate')}
                        info={t('bankOnboarding.finalize.syncDateInfo', { maxDate: todayLabel })}
                        required={true}
                        max={todayISO}
                        errors={get(errors, `accounts.${index}.begin_sync_from`)}
                        {...register(`accounts.${index}.begin_sync_from`, {
                          required: { value: true, message: t('validation.required') },
                          validate: value =>
                            !value || value <= todayISO
                              ? true
                              : t('bankOnboarding.finalize.syncDateInFuture', {
                                  maxDate: todayLabel
                                }),
                          onChange: () => {
                            clearErrors(`accounts.${index}.begin_sync_from`)
                            setSubmitMessages(null)
                          }
                        })}
                      />

                      <FeatureWrapper feature="payment_initiation">
                        <SubHeader>{t('holviConnection.ownerSettings.title')}</SubHeader>
                        <AuthorizedPaymentDescription>
                          {t('holviConnection.ownerSettings.description')}
                        </AuthorizedPaymentDescription>
                        <Controller
                          name={`accounts.${index}.account_admins`}
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <UserPicker
                              header={t('holviConnection.ownerSettings.payerSelectionTitle')}
                              value={[account.account_owner, ...value]}
                              disabled={account.account_owner !== currentUser.id}
                              onChange={value => {
                                onChange(value)
                              }}
                              renderAddButton={edit => (
                                <Button type="button" onClick={() => edit()} icon={<FaUserPlus />}>
                                  {t('holviConnection.ownerSettings.button')}
                                </Button>
                              )}
                              renderItem={({ user_id, email }) =>
                                user_id === account.account_owner ? (
                                  <TextTooltip
                                    tooltip={t('holviConnection.ownerSettings.tooltip.owner')}
                                  >
                                    <Badge type="info" key={user_id}>
                                      <FaShieldAlt />
                                      <span>{email}</span>
                                    </Badge>
                                  </TextTooltip>
                                ) : (
                                  <TextTooltip
                                    tooltip={t('holviConnection.ownerSettings.tooltip.admin')}
                                  >
                                    <Badge type="info" key={user_id}>
                                      <FaUserCheck />
                                      <span>{email}</span>
                                    </Badge>
                                  </TextTooltip>
                                )
                              }
                            />
                          )}
                        />
                      </FeatureWrapper>
                    </>
                  )}
                </StyledAccountWrapper>
              )
            })}
            <Spacer />
          </AnimatedContentLoader>
        </StyledForm>
      </SlideContent>
      <SlideFooter>
        <Button
          type="button"
          icon={<FaArrowLeft />}
          showSpinner={isDeleting}
          disabled={isDeleting || isSubmitting}
          onClick={() => handleGoBack()}
        >
          {t('general.back')}
        </Button>
        <Spacer />
        <Button
          type="submit"
          intent="success"
          icon={<FaCheck />}
          form={FINALIZE_FORM_ID}
          disabled={isAccountsLoading || isFormLoading || isSubmitting}
          showSpinner={isSubmitting}
        >
          {isEdit ? t('general.save') : t('bankOnboarding.finalize.activate')}
        </Button>
      </SlideFooter>
    </>
  )
}

const SubmitErrorList = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
`

const StyledForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const StyledAccountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 3px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  overflow: hidden;

  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;

  .input-wrapper {
    margin: 0;

    input {
      height: 1.6rem;
    }
  }
`

const StyledAccountHeaderWrapper = styled.div`
  display: flex;
  width: 100%;

  label {
    width: 100%;
    flex: 1;
    margin: 0;
  }

  .switch-wrapper {
    margin: 0;
  }
`

const StyledAccountTitle = styled.div`
  color: ${({ theme }) => theme.colors.metalGray};
`

const StyledAccountIdentifier = styled.div`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const LogoWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md}rem;
  justify-content: space-around;
  align-items: center;
  margin: ${({ theme }) => theme.spacing.xxl}rem 0;

  svg {
    width: 2rem;
    height: 2rem;
    min-width: 1rem;
    min-height: 1rem;
    fill: ${({ theme }) => theme.colors.neutralBlack};
  }
`

const LogoImg = styled.div<{ $src: string }>`
  width: 100%;
  height: 40px;
  background-image: ${({ $src }) => `url(${$src})`};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  &.right {
    height: 55px;
  }
`

const SubHeader = styled.h4`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
`

const AuthorizedPaymentDescription = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`
